import React, { useState, useEffect } from "react";
import moment from "moment";
import { useResourcesPlanner } from "../ResourcesPlannerContext";
import { navigateDateRange, getDefaultDateRange } from "../utils/dateUtils";
import { ArrowLeft, ArrowRight, SearchIcon, Cross, ArrowDownMedium } from "../../../modules/default-svg";
import CustomButton from "../../../components/controls/button/Button";
import CustomPopover from "../../../components/popover/Popover";
import IconInput from "../../../components/controls/input/iconInput/IconInput";
import UserSelect from "../../../components/userSelect/UserSelect";
import { apiCall } from "../../../store/services/service";
import { BASE_URL } from "../../../store/services/URL";
import styles from "../ResourcesPlanner.module.scss";
import { UserSearchRefType } from "../../../helpers/utils";

export default function PlannerHeader() {
  const {
    viewType,
    setViewType,
    dateRange,
    setDateRange,
    projectFilter,
    setProjectFilter,
    resourceFilter,
    setResourceFilter,
    scrollToToday,
  } = useResourcesPlanner();

  const [projectPopoverOpen, setProjectPopoverOpen] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    if (projectPopoverOpen) {
      fetchProjects(projectSearch);
    }
  }, [projectSearch, projectPopoverOpen]);

  const fetchProjects = (search) => {
    const url = `${BASE_URL}search/projects${search ? `?searchTerm=${search}` : ""}`;
    apiCall
      .get({ url, isAuthToken: true })
      .then((res) => setProjects(res || []))
      .catch(() => setProjects([]));
  };

  // ── Format period label ─────────────────────────────────────────────────
  const periodLabel = (() => {
    const start = moment(dateRange.startDate);
    const end = moment(dateRange.endDate);
    if (start.year() === end.year()) {
      return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
    }
    return `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`;
  })();

  const handlePrev = () => {
    setDateRange(navigateDateRange(viewType, dateRange.startDate, dateRange.endDate, -1));
  };

  const handleNext = () => {
    setDateRange(navigateDateRange(viewType, dateRange.startDate, dateRange.endDate, 1));
  };

  const handleToday = () => {
    const defaultRange = getDefaultDateRange(viewType);
    // Only reset date range if it has actually changed – avoids a spurious refetch
    if (dateRange.startDate !== defaultRange.startDate || dateRange.endDate !== defaultRange.endDate) {
      setDateRange(defaultRange);
    }
    scrollToToday();
  };

  const handleViewTypeChange = (type) => {
    setViewType(type);
  };

  return (
    <div className={styles.header}>
      <div className={styles.headerLeft}>
        <h2 className={styles.headerTitle}>Resources Planner</h2>

        {/* Project filter */}
        <CustomPopover
          width="320px"
          icon={
            <CustomButton
              btnClass={`${styles.filterBtn} btn_combo btn_combo_white ${projectFilter && styles.activeFilterBtn}`}
              label={projectFilter ? projectFilter.name : "All Projects"}
              clickHandler={() => setProjectPopoverOpen(!projectPopoverOpen)}
              rightIcon={<ArrowDownMedium />}
            />
          }
          status={projectPopoverOpen}
        >
          <div className="d-flex flex-column gap-3">
            <div className="d-flex justify-content-between align-items-center">
              <h6>Filter by Project</h6>
              <div onClick={() => setProjectPopoverOpen(false)} className="cursor-pointer">
                <Cross />
              </div>
            </div>
            <IconInput
              icon={<SearchIcon />}
              placeholder="Search"
              handleChange={setProjectSearch}
            />
            {projectFilter && (
              <div
                className={styles.clearFilter}
                onClick={() => {
                  setProjectFilter(null);
                  setProjectPopoverOpen(false);
                }}
              >
                Clear filter
              </div>
            )}
            <div style={{ maxHeight: "200px", overflow: "auto" }}>
              <div className="d-flex flex-wrap gap-2">
                {projects
                  .filter((p) => p.id !== projectFilter?.id)
                  .map((p) => (
                    <span
                      key={p.id}
                      className={styles.filterItem}
                      onClick={() => {
                        setProjectFilter({ id: p.id, name: p.name });
                        setProjectPopoverOpen(false);
                      }}
                    >
                      {p.name}
                    </span>
                  ))}
              </div>
            </div>
          </div>
        </CustomPopover>

        {/* Resource filter */}
        <div className={styles.resourceFilterWrapper}>
          <UserSelect
           className={styles.plannerResourceSelect}
           selectedUsersDisplayMode="avatar-only"
           selectedUsersDisplayLimit={6}
            multiple
            isAdminSearch
            refType={projectFilter ? UserSearchRefType.Project : null}
            refId={projectFilter ? projectFilter?.id : null}
            value={resourceFilter}
            onChange={(users) => setResourceFilter(users || [])}
            buttonLabel="Filter Resources"
            searchPlaceholder="Search resources"
          />
        </div>
      </div>

      <div className={styles.headerRight}>
        {/* Date navigation + range picker */}
        <div className={styles.dateNav}>
          <button className={styles.navBtn} onClick={handlePrev}>
            <ArrowLeft />
          </button>

          <span className={styles.dateRangeLabel}>{periodLabel}</span>

          <button className={styles.navBtn} onClick={handleNext}>
            <ArrowRight />
          </button>
        </div>

        <CustomButton btnClass={`todayBtn ${styles.todayBtn}`} label="Today" clickHandler={handleToday} />

        {/* View type toggle */}
        <div className={styles.viewToggle}>
          {["day", "week", "month"].map((type) => (
            <button
              key={type}
              className={`${styles.viewBtn} ${viewType === type ? styles.viewBtnActive : ""}`}
              onClick={() => handleViewTypeChange(type)}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
